import { deletePopup, createSigPopup, updateRequestSignatureContent } from 'actions';

import FormSwiftAPIClient from 'FormSwiftAPI/FormSwiftAPIClient';

const formSwiftAPIClient = new FormSwiftAPIClient(window.API_URL);

require('es6-promise').polyfill();
require('isomorphic-fetch');

const uuidV4 = require('uuid/v4');

// Maps the client signature type to the file api signature type
const typeToField = (type) => {
  const map = {
    type: 'typed_signature',
    draw: 'drawn_signature',
    upload: 'uploaded_signature',
  };

  return map[type];
};

// Converts JSON to FormData
const jsonToFormData = (json) => {
  const formPayload = new FormData();
  Object.keys(json).forEach((key) => {
    formPayload.append(key, json[key]);
  });

  return formPayload;
};

const signatureFormat = (typed, drawn, uploaded) => (
  {
    signatures: {
      typed: typed || null,
      drawn: drawn || null,
      uploaded: uploaded || null,
    },
  }
);

const formatPreferences = (json) => {
  const preferences = json.data.preferences;
  const {
    typed_signature: typed,
    drawn_signature: drawn,
    uploaded_signature: uploaded,
  } = preferences;

  return signatureFormat(typed, drawn, uploaded);
};

// Dispatches immediately after a get signatures request
const getSignaturesRequest = () => (
  (dispatch) => {
    dispatch({ type: 'GET_SIGNATURES_REQUEST' });
  }
);

// Dispatches if a get signatures request returns successfully
const getSignaturesSuccess = (res, isSingleSignatureMode = false) => (
  dispatch => dispatch({ type: 'GET_SIGNATURES_SUCCESS', res, isSingleSignatureMode })
);

// Dispatches if a get signatures request fails
const getSignaturesFailure = err => (
  dispatch => dispatch({ type: 'GET_SIGNATURES_FAILURE', err })
);

/*
Gets URLs for saved signature images for the current user
Dispatches:
  - getSignaturesRequest
  - getSignaturesSuccess
  - getSignatureFailure
Request:
  - /user/<user_id>/preferences
*/
export const getSignatures = (isSingleSignatureMode = false) => (
  async (dispatch) => {
    dispatch(getSignaturesRequest());

    const userId = window.COOKIE.get('userid');
    const userToken = window.COOKIE.get('userToken');
    const payload = { user_token: userToken };

    if (!userId) {
      const nullSignatures = signatureFormat(null, null, null);
      dispatch(getSignaturesSuccess(nullSignatures, isSingleSignatureMode));
      return Promise.resolve(nullSignatures);
    }

    return formSwiftAPIClient.request('GET', `/user/${userId}/preferences`, payload)
      .then((json) => {
        if (json.status === 'success') {
          const formattedJSON = formatPreferences(json);
          dispatch(getSignaturesSuccess(formattedJSON, isSingleSignatureMode));
          return Promise.resolve(formattedJSON);
        }

        return Promise.reject(json);
      })
      .catch(error => dispatch(getSignaturesFailure(error)));
  }
);

/*
Saves an image's URL to a user's preferences as their signature for that type
Params:
  - imgUrl (string): the URL for the image
  - sigType (string): the type for the signature
Request:
  - /user/<user_id>/preferences
Returns:
  - Resolved promise with the image URL in the body
Throws:
  - Rejected promise with an error message describing the error encountered
*/
export const saveSignatureUserPreference = (imgUrl, sigType) => {
  const field = typeToField(sigType);
  const userId = window.COOKIE.get('userid');
  const userToken = window.COOKIE.get('userToken');

  if (!userId) {
    return Promise.resolve(imgUrl);
  }

  const payload = {
    image_url: imgUrl,
    signature_type: field,
    user_token: userToken,
  };

  return formSwiftAPIClient.request('POST', `/user/${userId}/preferences`, payload)
    .then((json) => {
      if (json.status === 'success') {
        return Promise.resolve(imgUrl);
      }

      return Promise.reject('Unable to save signature to user preferences');
    })
    .catch(err => Promise.reject(err));
};

// Dispatches immediately after a save signature request
const saveSignatureRequest = () => (
  (dispatch) => {
    dispatch({ type: 'SAVE_SIGNATURE_REQUEST' });
  }
);

// Dispatches after a successful save signature request, including saving to the user's perferences
const saveSignatureSuccess = (imgUrl, sigType) => (
  dispatch => dispatch({ type: 'SAVE_SIGNATURE_SUCCESS', imgUrl, sigType })
);

// Dispatches if an error is encountered during an point of the save siganture request
const saveSignatureFailure = err => (
  dispatch => dispatch({ type: 'SAVE_SIGNATURE_FAILURE', err })
);

// Used in static builder signature manager to save one signature type
export const saveSingleSignature = (imgUrl, sigType) => (
  dispatch => dispatch({ type: 'SAVE_SINGLE_SIGNATURE', imgUrl, sigType })
);


/*
Handles a successfully saved image response from our API and saves the new URL
to the user's preferences
Params:
  - res (Response): a Response object from our save sig image API
  - sigType (string): the type for the signature
  - isSingleSignatureMode: for cases where we only want one signature to be active at a time
Dispatches:
  - saveSigantureUserPreference
  - saveSignatureSuccess
  - saveSigantureFailure
Returns:
  - Resolved promise with the signature image URL as the body
*/
const saveSignatureImageSuccess = (res, sigType, isSingleSignatureMode) => (
  (dispatch) => {
    const imgUrl = res.data.img_id;

    return saveSignatureUserPreference(imgUrl, sigType)
      .then(() => {
        if (isSingleSignatureMode) {
          dispatch(saveSingleSignature(imgUrl, sigType));
        } else {
          dispatch(saveSignatureSuccess(imgUrl, sigType));
        }
        return Promise.resolve(imgUrl);
      })
      .catch(err => dispatch(saveSignatureFailure(err, sigType)));
  }
);

/*
Saves a signature image to our File API
Params:
  - payload (object): the JSON formatted payload for our File API
  - sigType (string): the type for this signature
Dispatches:
  - saveSignatureRequest
  - saveSigantureImageSuccess
  - saveSignatureFailure
Request:
  - /save-sig-image
*/
export const saveSignature = (payload, sigType, isSingleSignatureMode) => (
  (dispatch) => {
    dispatch(saveSignatureRequest());
    const formPayload = jsonToFormData(payload);

    return fetch('/free-write/scripts/save-sig-image.php', {
      method: 'POST',
      body: formPayload,
    })
      .then(response => response.json())
      .then((json) => {
        if (json.status === 'ok') {
          return Promise.resolve(
            dispatch(saveSignatureImageSuccess(json, sigType, isSingleSignatureMode)),
          );
        }

        return Promise.reject(json.message);
      })
      .catch(err => (
        dispatch(saveSignatureFailure(err))
      ));
  }
);

/*
Converts the content of the type signature preview input field to the save signature
payload for the File API and dispatches a save signature request
*/
export const saveTypeSignature = (val, elmId = 'sig-text', isSingleSignatureMode = false) => (
  (dispatch) => {
    // Create hidden canvas
    const textCanvas = document.createElement('canvas');
    const textCanvasCTX = textCanvas.getContext('2d');
    textCanvasCTX.font = '60px Allura';
    document.getElementById(elmId).appendChild(textCanvas);

    // Set canvas width
    const textWidth = textCanvasCTX.measureText(`${val}  `).width.toFixed(0);
    textCanvasCTX.canvas.width = textWidth;
    textCanvasCTX.canvas.height = 75;
    textCanvas.setAttribute('style', `width: ${textWidth}px; height: 75px;`);
    // Hiding the canvas so it doesn't show up on the bottom of the document
    textCanvas.setAttribute('style', 'display: none;');

    // Fill text into canvas
    textCanvasCTX.font = '60px Allura';
    textCanvasCTX.fillText(val, 0, 50);

    // Save the image
    const signaturePayload = {
      sig_image: textCanvas.toDataURL(),
    };

    return dispatch(saveSignature(signaturePayload, 'type', isSingleSignatureMode));
  }
);

// Dispatches immediately after a remove signature request is envoked
const removeSignatureRequest = () => (
  (dispatch) => {
    dispatch({ type: 'REMOVE_SIGNATURE_REQUEST' });
  }
);

// Dispatches after a remove signature request is successful
const removeSignatureSuccess = sigType => (
  (dispatch) => {
    dispatch({ type: 'REMOVE_SIGNATURE_SUCCESS', sigType });
  }
);

// Dispatches if a remove signature request encounters an error
const removeSignatureFailure = (err, sigType) => (
  (dispatch) => {
    dispatch({ type: 'REMOVE_SIGNATURE_FAILURE', err, sigType });
  }
);

/*
Removes a signature from a user's preferences
Params:
  - sigType (string): the type of signature to remove
Dispatches:
  - removeSignatureRequest
  - saveSignatureUserPreference
  - removeSignatureSuccess
  - removeSignatureFailure
*/
export const removeSignature = sigType => (
  (dispatch) => {
    dispatch(removeSignatureRequest());
    return saveSignatureUserPreference('', sigType)
      .then(() => {
        dispatch(removeSignatureSuccess(sigType));
      })
      .catch((err) => {
        dispatch(removeSignatureFailure(err, sigType));
      });
  }
);

/*
Replaces a popup with text content with a type signature popup with the same content
in the same position on the builder
Params:
  - popupID (string): the ID of the popup to be replaced
  - imgSrc (string): the source for the image to replace the popup
Dispatches:
  - updateRequestSignatureContent
  - deletePopup
  - createSigPopup
  - closeModal
*/
export const replaceSignaturePopup = (popupID, imgSrc, sigType = null) => (
  (dispatch, getState) => {
    const replacementData = getState().popupHandler.popups[popupID];
    const { x, y } = replacementData;

    const img = new Image();
    img.src = imgSrc;
    img.onload = () => {
      if (replacementData.type === 'request-signature' && replacementData.contentSettings.type === 'image') {
        dispatch(updateRequestSignatureContent(popupID, imgSrc, sigType));
      } else {
        dispatch(deletePopup(popupID));
        dispatch(createSigPopup(imgSrc, uuidV4(), window.getPageIndex(), img.width, x, y));
      }
    };
  }
);

/*
Expands the free write signature manager and prepares the popupID passed to be
replaced by the selected signature in the modal
Params:
  - popupID (string): the ID of the popup to be replaced
Dispatches:
  - openFromPopup
*/
export const loadPopupIdToSignatureManager = (popupID, signExpanded = true) => (
  (dispatch) => {
    setTimeout(window.setMainContainerHeight, 10);
    dispatch({ type: 'SIGNATURE_MANAGER_POPUPID_LOAD', popupID, signExpanded });
  }
);

/*
Expands the free write signature manager without a popupID
Params:
  - none
Dispatches:
  - showSignatureSection
*/

export const expandFreeWriteSignatureSection = () => (
  (dispatch) => {
    setTimeout(window.setMainContainerHeight, 10);
    dispatch({ type: 'SIGNATURE_SECTION_SHOW' });
  }
);

/*
Expands the free write signature manager, update the active button
and open the Add Signature section corresponding to signatureType
Params:
  - signatureType (string): the type of signature to be added (either
  'type', 'draw', and 'upload')
Dispatches:
  - showAddSignatureSection
*/

export const expandFreeWriteAddSignatureSection = signatureType => (
  (dispatch) => {
    setTimeout(window.setMainContainerHeight, 10);
    dispatch({ type: 'ADD_SIGNATURE_SECTION_SHOW', signatureType });
  }
);

/*
Closes the free write signature manager without a popupID
Params:
  - none
Dispatches:
  - hideSignatureSection
*/
export const hideFreeWriteSignatureSection = () => (
  (dispatch) => {
    setTimeout(window.setMainContainerHeight, 10);
    dispatch({ type: 'HIDE_SIGNATURE_SECTION' });
  }
);

/*
Saves a text popup as a type signature to replace the text popup with a signature.
Params:
  - popupID (string): the ID of the popup being replaced
  - textVal (string): the value to save as a type signature
  - autoExpandSignatureManager (bool): a bool to determine if we expand signature
Dispatches:
  - saveTypeSignature
  - loadPopupIdToSignatureManager
*/
export const savePopupAsSignature = (popupID, textVal, autoExpandSignatureManager = true) => (
    async (dispatch) => {
      await dispatch(saveTypeSignature(textVal, 'viewer'));
      dispatch(loadPopupIdToSignatureManager(popupID, autoExpandSignatureManager));
    }
);



// WEBPACK FOOTER //
// ./src-react/SignatureManager/SignatureManagerActions.jsx